/** Custom hook to provide all the Media setup functionality */
import { useState, useRef, useEffect } from "react";
import useDigitalChannel from "./useDigitalChannel";
import useMediaPopOverSetup from "./useMediaPopOverSetup";
import { useTaskListStore, TASKLIST_ACTIONS } from "../../Store";
import { i18n } from '../../utils/GadgetUtils';
import useCallVariableProps from './useCallVariableProps';
import { cloneDeep } from 'lodash';

import { ALL_TASKS_COLECTION_LS_KEY, ACTIVE_TASK_LS_KEY, getTaskFromLS, saveTaskToLS, removeTaskFromLS, getFromLS , getMediaLayout} from "../../utils/TaskUtils";
import { FINESSE_NON_VOICE_SIGNED_IN_KEY, getFromSessionStorage, saveToSessionStorage, saveToLS } from '../../utils/cacheUtils';


// TODO: Desktop properties
var prefs = new gadgets.Prefs();

const activateNewTasksAutomatically = prefs.getString('activateNewTasksAutomatically') === '' ? true : prefs.getBool('activateNewTasksAutomatically');
const digitalChannelSignInFailureMaxRetries = prefs.getString('digitalChannelSignInFailureMaxRetries') ? prefs.getString('digitalChannelSignInFailureMaxRetries') : 5;
const digitalChannelSignInFailureRetryDelay = prefs.getString('digitalChannelSignInFailureRetryDelay') ? prefs.getString('digitalChannelSignInFailureRetryDelay'): 60000;
const loadingConversationFailureMaxRetries= prefs.getString('loadingConversationFailureMaxRetries') ? prefs.getString('loadingConversationFailureMaxRetries') : 4; 
const loadingConversationFailureRetryDelay = prefs.getString('loadingConversationFailureRetryDelay') ?  prefs.getString('loadingConversationFailureRetryDelay') : 3000;

var mediaPropertiesLayouts;
const CACHED_FINESSE_NON_VOICE_SIGNED_IN = !!getFromSessionStorage(FINESSE_NON_VOICE_SIGNED_IN_KEY);
console.log('Finesse-IMI Gadget : [useMediaSetup Hook] [] : CACHED_FINESSE_NON_VOICE_SIGNED_IN : ', CACHED_FINESSE_NON_VOICE_SIGNED_IN);

const useMediaSetup = (INITIAL_PROPS) => {
    const [loading, setLoading] = useState(true);
    const [signedIn, setSignedIn] = useState(false);
    const [retry, setRetry] = useState(false);
    const [error, setError] = useState(null);
    const [errorAfterTaskLoad, setErrorAfterTaskLoad] = useState(null);
    const [conversationLoader, setConversationLoader] = useState('');
    const [allMediaObjects, setAllMediaObjects] = useState([]);
    const GADGET_CONFIG = window.finesse.container.Config;
    const mediaPopOverSetup = useMediaPopOverSetup();
    const digitalChannelSetup = useDigitalChannel(INITIAL_PROPS);
    const [user, setUser] = useState({});
    const [wrapUpData, setWrapUpData] = useState({});
    const [scriptSelectorData, setScriptSelectorData] = useState({});
    const [userData, setUserData] = useState({});
    const [taskListState, taskListHandleActions] = useTaskListStore();
    let wrapUpReasons, delayRef;
    let useFinesseSimulator = prefs.getBool('use-finesse-imi-simulator');
    if (!useFinesseSimulator) {
        useFinesseSimulator = false;
    }

    const _useCallVariableProps = useCallVariableProps();


    const loginParameter = useRef(new Map()); // map that stores login parameter of each media object as key-value pair. key = media id and value = login parameters
    const allMediaObjectsRef = useRef(allMediaObjects);
    const mediaLayoutRef = useRef();
    const taskListStateRef = useRef(taskListState);
    const hasFncRegistered = useRef(false);
    const remainingRetries = useRef(loadingConversationFailureMaxRetries);
    const userDataRef = useRef(userData);
    const notReadyReasonCodesRef = useRef([]);
    const allDialogsLoadedMediaInfo = useRef({ids: [], mediaCount: 0});
    const taskStates = window.finesse.restservices.MediaDialog.ParticipantStates;
    const _participantActions = window.finesse.restservices.MediaDialog.TaskActions;
    const showMyGadgetNotification = window.finesse?.containerservices?.ContainerServices?.showMyGadgetNotification;
    // new replies count is stored in localstorage to show the count on reload
    let storedNewReplies = JSON.parse(localStorage.getItem('unreadmessages')) || {}; 
    const recoveredRef = useRef(false);
    const processedTasksStatus = useRef({});
    const userRef = useRef({});

    useEffect(() => {
        allMediaObjectsRef.current = allMediaObjects;
    }, [allMediaObjects]);

    useEffect(() => {
        taskListStateRef.current = taskListState;
    }, [taskListState]);

    useEffect(() => {
        userDataRef.current = userData;
    }, [userData]);
    
    useEffect(() => {
        // TODO: remove this. Adding this to debug the user states
        // window.user = user;
        userRef.current = user;
    }, [user]);

    useEffect(() => {
        console.log('::-  useMediaSetup INITIAL_PROPS.recovered changed', INITIAL_PROPS.recovered);
        recoveredRef.current = INITIAL_PROPS.recovered;

        // if (recoveredRef.current) {
        //     console.log('::-  useMediaSetup INITIAL_PROPS.recovered is true and now clearing store****');
        //     processedTasksStatus.current = {}; // reset the flags post recovery.
        //     taskListHandleActions({type: TASKLIST_ACTIONS.CLEAR});
        // }
    }, [INITIAL_PROPS.recovered]);

    function initialize() {
        // setup the clientservices to populate the id and auth in the finesse gadget config
        window.finesse.clientservices.ClientServices.init(GADGET_CONFIG);

        // initialize the finesse user
        const user = new finesse.restservices.User({
            id: GADGET_CONFIG.id,
            onLoad: handleUserLoad,
            onChange: handleUserChange
        });

        // Initialize the conversation listeners
        window.addEventListener("onConversationLoaded", (e) => {
            console.log("onConversationLoaded", e);
            remainingRetries.current = loadingConversationFailureMaxRetries;
            clearTimeout(delayRef);
            setConversationLoader('');
        });

        window.addEventListener("onConversationLoadedError", (e) => {
            console.log("onConversationLoadedError", e.detail);
            const errCode = e.detail.code;
            // errcode 9010 - user does not exist in the engage in the tenant (org ID)
            // errcode 9015 - Tried to load conversation prior to 'Add participant'
            if (errCode === '9010' || errCode === '9015') {
                if (remainingRetries.current) {
                    remainingRetries.current = remainingRetries.current - 1;
                    delayRef = setTimeout(() => {
                        window.imiEngageWC.reload();
                    }, loadingConversationFailureRetryDelay);                    
                    setConversationLoader(i18n.getMsg('gadget.imi.engage.errorLoading'));
                } else {
                    setConversationLoader('');
                }
            } else {
                setConversationLoader('');
            }
        });

        window.addEventListener("onMessageReceived", (e) => {
            console.log("onMessageReceived", e);
    
            const detail = e.detail;
            const task = findByConnversationId(detail.conversationId);
            if (task && task.taskId !== taskListStateRef.current.selectedTaskId) {
              console.log("onMessageReceived for task ", task);
              task.quantity = (parseInt(task.quantity) || 0)+1;
    
              taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
    
              showMyGadgetNotification && showMyGadgetNotification({
                messageFrom: task.title,
                message: detail.message,
                isDismissable: true,
                timeout: 8000
              });
            }
          });

        window.addEventListener("LIVECHAT_CHAT_CLOSED", (e) => {
            console.log('chat is ended by customer', e);
            const detail = e.detail;
            const task = findByConnversationId(detail.conversationId);
            if (task) {
                task.disableTransfer = true;
                taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
            }
        });

        /* Store umread-messages/new-replies count in localstorage on refresh */
        window.addEventListener('beforeunload', (event) => {
            updateCacheBeforeDisconnect();
        });

    }

    function findByConnversationId(id) {
        return taskListStateRef.current.taskList.find(task => task.conversationid === id);
    }

    function handleUserLoad(user) {
        setUser(user);

        let userDataObj = {};
        userDataObj.wrapUpOnIncoming = user.getWrapUpOnIncoming();
        userDataObj.wrapUpOnOutgoing = user.getWrapUpOnOutgoing();
        userDataObj.isWrapUp = user.isWrapUp();
        userDataObj.isWrapUpRequired = user.isWrapUpRequired();
        userDataObj.wrapUpTimer = user.getWrapUpTimer();
        userDataObj.stateChangeTime = user.getData().stateChangeTime;
        console.log(':: handleUserLoad userDataObj- ', userDataObj);
        setUserData(userDataObj);

        mediaPropertiesLayouts = user.getMediaPropertiesLayouts({
            onLoad: handleMediaPropertiesLayoutsLoad,
            onError: handleMediaPropertiesLayoutsError
        });
        const mediaList = user.getMediaList({
            onLoad: handleMediaListLoad
        });
        user.getNotReadyReasonCodes({
			success: function(reasonCodes) {
                notReadyReasonCodesRef.current = reasonCodes;
                console.log('useMediaSetup successfully fetched not ready reason codes ', notReadyReasonCodesRef.current, reasonCodes);
            } ,
			error: function (err) {
				console.error('Error while fetching not ready reason codes ', err);
			}
		});
        wrapUpReasons = user.getWrapUpReasons({
            onLoad: handleWrapUpReasonsLoaded,
            onError: handleWrapUpReasonsError
        });
        const scriptSelectors = new finesse.restservices.ScriptSelectors({ 'mrdType' : 'digitalchannels',
            onLoad: handleScriptSelectorsLoaded,
            onError: handleScriptSelectorsError
        });
    }

    function handleUserChange(user) {
        console.log('User state changed', user);
    }

    function handleMediaPropertiesLayoutsLoad(layout) {
        // TODO: extract the call variable layout config for user.Layout if available, otherwise fallback to default
        mediaLayoutRef.current = layout.getCollection();
        console.log('::- useMediaSetup- handleMediaPropertiesLayoutsLoad ', layout, layout.getCollection(), mediaLayoutRef);
    }

    // eslint-disable-next-line handle-callback-err
    function handleMediaPropertiesLayoutsError(error) {
        console.log('::- useMediaSetup- handleMediaPropertiesLayoutsError ', error);

        // TODO: handle error
    }

    function handleMediaListLoad(_mediaList) {
        if (_mediaList.length === 0) {
            setError({
                title: i18n.getMsg('gadget.imi.gadgetLoad.failure.accessDenied.title'),
                description: i18n.getMsg('gadget.imi.gadgetLoad.failure.accessDenied.description')
            });
            console.error('No MRDs configured for this user');
            return;
        }

        loadMediaDomainList({onLoad: onMediaDomainLoad});

        function onMediaDomainLoad(mediaDomain) {
            // Now that we have the media list, initialize each media object with appropriate listeners (load, change, error etc)
            const medialist = configureAllAvailableMedia(_mediaList, mediaDomain);

            if (medialist.length === 0) {
                setError({
                    title: i18n.getMsg('gadget.imi.gadgetLoad.failure.accessDenied.title'),
                    description: i18n.getMsg('gadget.imi.gadgetLoad.failure.accessDenied.description')
                });
                console.error('No MRDs configured for this user');
                return;
            }

            // Now that we have the media Channel list, do the the FNC registration
            if (!hasFncRegistered.current){
                FNCRegistration(_mediaList, mediaDomain, medialist);
            }

            // Check if the Media is already logged in when loading the gadget.
            checkMediaStateOnLoad(medialist);
        }
    }

    function checkMediaStateOnLoad(medialist) {
        // on load filter the channels which are logged out
        const loggedOutMediaList = medialist.filter(media => {
            return !media.isLoggedIn();
        });
        console.log('Finesse-IMI Gadget : [useMediaSetup Hook] [checkMediaStateOnLoad] : loggedOutMediaList : ', loggedOutMediaList);

        // if all media are logged in already, don't show the Sign In Modal
        if(loggedOutMediaList.length === 0) {
            setSignedIn(true);
        } else if(loggedOutMediaList.length < medialist.length) {
            setSignedIn(true);
            // if some of the channels are logged out when reloading then don't show the sign In Modal
            // handle those logged out channels in the background by trying to signing into them ?
            handleLoggedOutMediaOnLoad(loggedOutMediaList);
        } else if (loggedOutMediaList.length && window.top.finesse.container.Config.hasFailedover && CACHED_FINESSE_NON_VOICE_SIGNED_IN) {
            // if all media loggedout on failover, make autoLogin for the medias and only if FINESSE_NON_VOICE_SIGNED_IN_KEY flag is true. This flag comes from previous node to failedover node.
            console.log('Finesse-IMI Gadget : [useMediaSetup Hook] [checkMediaStateOnLoad] : hasFailedover is true cache and CACHED_FINESSE_NON_VOICE_SIGNED_IN is true and therefore calling handleLoggedOutMediaOnLoad() onLoad : ', loggedOutMediaList);

            handleLoggedOutMediaOnLoad(loggedOutMediaList);
        }
        setLoading(false);

    }

    function handleLoggedOutMediaOnLoad(loggedOutMediaList) {
        handleMediaLogin(null, loggedOutMediaList)
        // retry login for these media channels?
    }

    function mediaLogin(media) {
        return new Promise((resolve, reject) => {
            const params = getMediaLoginParams(media, resolve, reject);
            media.login(params);
        })
    }

    async function handleMediaLogin(event, loggedOutMediaList) {
        console.log(`Finesse-IMI Gadget: handleMediaLogin- `, event, loggedOutMediaList);

        const mediaObjects = loggedOutMediaList || allMediaObjects;
        setLoading(true);
        // results of the initial media login requests
        const results = await Promise.allSettled(mediaObjects.map(media => mediaLogin(media)));

        console.log(`Finesse-IMI Gadget: handleMediaLogin, login results- `, results);

        // Invoke signin related notification
        digitalChannelSetup.handleDesktopNotification(results, false);

        // status array for all media ['fullfilled', 'fullfiled', 'rejected'....]
        const statusArray = results.map(result => result.status);

        //TODO: invoke FNC refresh after failback login
        if (recoveredRef.current && statusArray.filter(status => status === 'fulfilled').length !== 0) {
            // digitalChannelSetup.restoreFncStatesFromCache();
        }

        // Ff all media fails to login for the first time, show the gadget-level retrying UI
        if(statusArray.filter(status => status === 'fulfilled').length === 0 && mediaObjects.length === allMediaObjects.length) {
            setRetry(true);
            // TODO: invoke all signin failed notification
        }
        let loginRetryingMedias = [];
        // start retrying for failed media login requests
        results.forEach((result, index) => {
            //TODO: send UI notification for all the media retry
            if (result.status === 'rejected') {
                loginRetryingMedias.push(retry(mediaLogin, digitalChannelSignInFailureMaxRetries, index));
            }
        });
        setLoading(false);

        const retryResults = await Promise.allSettled(loginRetryingMedias);
        console.log(`Finesse-IMI Gadget: retryResults- `, retryResults);

        // Invoke signin related notification
        digitalChannelSetup.handleDesktopNotification(retryResults, true);

        // updates the status array with every resolve or reject
        // example -> ['fullfilled', 'rejected'] => ['fullfilled', 'fullfiled']
        function updateStatus(index, state) {
            statusArray[index] = state;
            console.log(`Finesse-IMI Gadget: updateStatus- ${index}... (${state})`, retryResults);

            // TODO: send UI notification for success or failure for the retry
        }
        /**
         * Retries the media login for a given number of times with a given delay
         * @param {Function} operation Media login operation
         * @param {String} maxRetries Maximum number of retries allowed 
         * @param {String} index Media index in the status array
         */
        function retry (operation, maxRetries, index) {
            console.log(`Finesse-IMI Gadget: Entering retry`);

            const media = mediaObjects[index];
            const mediaId = media.getId();
            let numberOfTries = 0;
            return new Promise((resolve, reject) => {
              const interval = setInterval(async () => {
                numberOfTries++;
                if (numberOfTries === maxRetries) {
                  console.log(`Finesse-IMI Gadget: Trying for the last time for ${mediaId}... (${maxRetries})`);
                  clearInterval(interval);
                }
                try {
                    console.log(`Finesse-IMI Gadget: try block `, media, media.isLoggedIn(), media.getState());

                  await operation(media);
                    clearInterval(interval);
                    console.log(`Finesse-IMI Gadget: Media signin successful for ${mediaId}, retried ${numberOfTries} times.`);
                    resolve({mediaId: media.getId()});
                    // update on success
                    updateStatus(index, 'fullfilled');
                } catch (err) {
                  console.log(`Finesse-IMI Gadget: Media Signin unsuccessful for ${mediaId}, retried ${numberOfTries} times... `, err);
                  // update only if the max retries have exhausted and signin still failed
                  if (numberOfTries === maxRetries) {
                    updateStatus(index, 'rejected');
                    reject({mediaId: media.getId()});
                  }
                }
              }, digitalChannelSignInFailureRetryDelay);
            });
          }
    }

    function getMediaLoginParams(media, resolve, reject) {
        const mediaOptions = loginParameter.current.get(media.getId());
        return {
            maxDialogLimit: mediaOptions.maxDialogLimit,
            interruptAction: mediaOptions.interruptAction,
            dialogLogoutAction: mediaOptions.dialogLogoutAction,
            handlers: {
                error: function (res) {
                    console.log('Finesse-IMI Gadget: Media login error!!', media.getId(), res);
                    if (res.object?.ApiErrors?.ApiError?.ErrorMessage === "E_ARM_STAT_AGENT_ALREADY_LOGGED_IN" ) {
                        console.log('Finesse-IMI Gadget: Media login error because of E_ARM_STAT_AGENT_ALREADY_LOGGED_IN. Marking as success', media.getId(), res);
                        resolve({mediaId: media.getId()});
                    } else {
                        // Show error notification and warning symbol on the FNC
                        digitalChannelSetup.handleSigninFailure(media.getId());
                        reject({mediaId: media.getId()});
                    } 
                },
                success: function (res) {
                    console.log('Finesse-IMI Gadget: Media login success!!', media.getId(), res);

                    // sign in and show the gadget if at least one channel successfully logs in
                    if (!signedIn) {
                        setSignedIn(true);
                    }
                    if (retry) {
                        setRetry(false);
                    }
                    resolve({mediaId: media.getId()});
                }
            }
        };
    }

    function loadMediaDomainList(callbacks) {
        const MediaDomain = new finesse.restservices.MediaDomains({
            onLoad: callbacks.onLoad,
        });
    }

    function FNCRegistration(mediaList, mediaDomain, medialistObj) {
        console.log('::- useMediaSetup- FNCRegistration ', mediaList, mediaDomain, medialistObj);
        const mediaDomainList = filterMediaDomain(mediaDomain.getData()?.MediaDomain);
        let mediaListData = mediaList.getData()?.Media;
        mediaListData = Array.isArray(mediaListData) ? mediaListData : [mediaListData];
        function filterMediaDomain (mediaDomainList) {
            return mediaDomainList?.filter((mediaDomain) => mediaDomain.id !== '1' && mediaDomain.finesseAssociated === "true" && mediaDomain.channel.toLowerCase() !== 'unknown' && mediaDomain.mediaClass && mediaDomain.mediaClass.toLowerCase() !== 'unknown');
        }

        function registerFncChannels () {
            digitalChannelSetup.initialize(mediaListData, mediaDomainList, medialistObj, notReadyReasonCodesRef.current);
            digitalChannelSetup.addAllChannels();
            hasFncRegistered.current = true;
        };
        registerFncChannels();
        finessePopoverRegistration(mediaDomainList);
    }

    function finessePopoverRegistration(mediaDomainList) {
        mediaPopOverSetup.init(mediaLayoutRef.current, digitalChannelSetup, mediaDomainList);
    }

    function configureAllAvailableMedia(mediaList, mediaDomain) {
        const mediaDomainList = mediaDomain.getData()?.MediaDomain;
        function validateMediaDomain (id) {
            let mediaDomain = mediaDomainList.find((media) => media.id === id);
            return id !== '1' && mediaDomain.finesseAssociated === "true" && mediaDomain.channel.toLowerCase() !== 'unknown' && mediaDomain.mediaClass && mediaDomain.mediaClass.toLowerCase() !== 'unknown';
        }
        const mrdIds = Object.keys(mediaList.getCollection()).filter(id => validateMediaDomain(id));
        const list = mrdIds.map(mrdId => mediaList.getMedia({
            id: mrdId,
            onLoad: handleMediaLoad,
            onError: handleMediaError,
            onChange: handleMediaChange
        }))
        setAllMediaObjects(list);

        getLoginParameters(list);
        return list;
        //handleMediaLogin();
    }

     // extracts login parameters that are set in desktop layout of cfadmin
     function getLoginParameters(mediaObjects) {
        // use these default login parameters if there is no valid parameter set in desktop layout of cfadmin
        const defaultMediaOptions = {
            maxDialogLimit: 5,
            interruptAction: 'IGNORE',
            dialogLogoutAction: 'TRANSFER',
            routable: true
        };

        let globalMaxDialogLimit = prefs.getString('maxDialogLimit');
        let globalDialogLogoutAction = prefs.getString('dialogLogoutAction');

        if(globalMaxDialogLimit && globalMaxDialogLimit <= 10 && globalMaxDialogLimit > 0){
            defaultMediaOptions.maxDialogLimit= globalMaxDialogLimit;
        }
       
        if(globalDialogLogoutAction){
            if(globalDialogLogoutAction.toUpperCase() === 'CLOSE' || globalDialogLogoutAction.toUpperCase() === 'TRANSFER'){
                defaultMediaOptions.dialogLogoutAction= globalDialogLogoutAction.toUpperCase();
            }  
        }

            mediaObjects.map(media => 
                {
                    const mediaOptions = cloneDeep(defaultMediaOptions);
                    var id = media.getId();

                    if(prefs.getString('maxDialogLimit-'+id) && prefs.getString('maxDialogLimit-'+id) <= 10 && prefs.getString('maxDialogLimit-'+id) >0) {
                        mediaOptions.maxDialogLimit = prefs.getString('maxDialogLimit-'+id);
                    }
                    // The interruptAction passed can only be IGNORE for a non-interruptible MRD
                    if(prefs.getString('interruptAction-'+id) && media.getInterruptible() === true) {
                        if(prefs.getString('interruptAction-'+id).toUpperCase() === 'ACCEPT' || prefs.getString('interruptAction-'+id).toUpperCase() === 'IGNORE'){
                            mediaOptions.interruptAction = prefs.getString('interruptAction-'+id).toUpperCase();
                        }
                    }
                    else if(prefs.getString('interruptAction') && media.getInterruptible() === true){
                        if(prefs.getString('interruptAction').toUpperCase() === 'ACCEPT' || prefs.getString('interruptAction').toUpperCase() === 'IGNORE'){
                            mediaOptions.interruptAction= prefs.getString('interruptAction').toUpperCase();
                        }  
                    }
                    if(prefs.getString('dialogLogoutAction-'+id)) {
                        if(prefs.getString('dialogLogoutAction-'+id).toUpperCase() === 'CLOSE' || prefs.getString('dialogLogoutAction-'+id).toUpperCase() === 'TRANSFER'){
                            mediaOptions.dialogLogoutAction = prefs.getString('dialogLogoutAction-'+id).toUpperCase();
                        }
                    }
                    loginParameter.current.set(id, mediaOptions);
                })

        console.log('useMediaSetup- loginParameters = ', loginParameter.current);
    }


    function handleMediaLoad(media) {
        console.log('Media loaded', media);
        // TODO: implement based on the media channel type if required ?
        loadMediaDialogs(media);
    }

    function handleMediaError(media) {
        console.log('Media error', media);
        // TODO: implement based on the media channel type if required ?
        // TODO: Show error in notification and traingle(warning) on FNC for the specific media channel which failed to load as per the UX
        // TODO: Retrying logic ?
    }

    function hasAllMediasLoggedOut() {
        if (allMediaObjectsRef.current) {
            const result = allMediaObjectsRef.current.filter((mediaObj) => {
                // const mediaObj = getMediaObj(channelId);
                return !mediaObj.isLoggedIn();
            });
            return result.length === allMediaObjectsRef.current.length;
        }
        return false;
    }

    function handleMediaChange(media) {
        console.log(':: Media change', media, media.getState(), media.isRoutable());
        digitalChannelSetup.handleMediaChange(media);
        if (hasAllMediasLoggedOut()) {
            console.log(':: Media change. all medias are loggedout and hence setting signedIn state to false.', media, media.getState(), media.isRoutable());

            // saveToSessionStorage(FINESSE_NON_VOICE_SIGNED_IN_KEY, false); // incase of agent logout
            setSignedIn(false);
        }
        // Gets all list of tasks of the interrupted MRD
        if (media.getState() === 'INTERRUPTED'){
            taskListStateRef.current.taskList && 
            taskListStateRef.current.taskList.forEach((task) => {
                    if (task.mrdID === media.getId()) {
                        task.isInterrupted = true;
                        task && taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task: task });
                    }
            });
        } else {
            taskListStateRef.current.taskList && 
            taskListStateRef.current.taskList.forEach((task) => {
                    if (task.mrdID === media.getId()) {
                        if(task.isInterrupted) {
                            task.isInterrupted = false;
                            task && taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task: task });
                        }
                        
                    }
            });
        }
        // TODO: implement based on the media channel type if required ?
    }

    function loadMediaDialogs(media) {
        const mediaDialogs = media.getMediaDialogs({
            onCollectionAdd: handleMediaDialogsAdd,
            onCollectionDelete: handleMediaDialogsDelete,
            onLoad: handleMediaDialogsLoad
        });
    }

    function handleMediaDialogsLoad(mediaDialogs) {
        /* Increment mediacount property to indicate that another media has loaded its dialogs
            Reason: there is no API to fetch all dialogs in a single server call
        */
        allDialogsLoadedMediaInfo.current.mediaCount += 1;

        const dialogCollection = mediaDialogs.getCollection();
        const activeTaskLS = getFromLS(ACTIVE_TASK_LS_KEY);
        const dialogIds = Object.keys(dialogCollection);
        // store the dialog Ids that have been loaded because there is no API to provide all dialogs in single call.
        allDialogsLoadedMediaInfo.current.ids.push(...dialogIds);

        
        for (let id in dialogCollection) {
            const dialog = dialogCollection[id];
            handleMediaDialogsAdd(dialog);
        }
        // Condition to ensure that all medias has loaded its respective dialogs
        if (allDialogsLoadedMediaInfo.current.mediaCount === allMediaObjectsRef.current.length) {
            if (activeTaskLS) {
                const isLsActiveTaskValid = allDialogsLoadedMediaInfo.current.ids.find((dialogId) => {
                    console.log(':: useMediaSetup- handleMediaDialogsLoad. cross-checking LS active task with loaded tasks....', dialogId, activeTaskLS, activeTaskLS.taskId);
                    return dialogId === activeTaskLS.taskId;
                });
                // if the task stored in localstorage is not valid anymore, the remove that task from localstorage
                if (!isLsActiveTaskValid) {
                    console.log(':: useMediaSetup- handleMediaDialogsLoad removing invalid active task from activeTaskConfig in LS....');
                    removeTaskFromLS(ACTIVE_TASK_LS_KEY, activeTaskLS.taskId);
    
                    // force render tasklist so as to update current active task to LS since the already active one was removed in above line. Can be removed once API for fetching all dialogs in single call is available
                    const task = taskListStateRef.current.taskList && taskListStateRef.current.taskList.find((task) => task.taskId === taskListStateRef.current.selectedTaskId);
                    task && taskListHandleActions({ type: TASKLIST_ACTIONS.SELECT, task: task });
                } else {
                    // const task = state.taskList && state.taskList.find((task) => task.taskId === taskId);
                        const task = taskListStateRef.current.taskList && taskListStateRef.current.taskList.find((task) => task.taskId === activeTaskLS.taskId);
                        if (task) {
                            console.log(':: useMediaSetup-  making the task active ', task);
                            taskListHandleActions({ type: TASKLIST_ACTIONS.SELECT, task: task });
                        }
                    
                }
            }
            
            // remove the tasks from LS, if they are not loaded anymore
            const allTasks = getFromLS(ALL_TASKS_COLECTION_LS_KEY);
            if (allTasks) {
                allTasks.forEach((task) => {
                    if (!allDialogsLoadedMediaInfo.current.ids.includes(task.taskId)) {
                        console.log(':: useMediaSetup- handleMediaDialogsLoad removing invalid active task from allTasksConfig in LS....', task.taskId);
                        removeTaskFromLS(ALL_TASKS_COLECTION_LS_KEY, task.taskId);
                    }
                });
            }

        }
    }

    function getMyParticipant (dialog) {
        const participant = dialog.getParticipants().filter(participant => participant.mediaAddress === window.finesse.container.Config.id);
        if (participant.length) {
            return participant[0];
        }
    }

    function isExistingTask(dialog) {
        return taskListStateRef.current.taskList.find(task => task.taskId === dialog.getId())
    }

    function checkIfMandatoryFieldsExist(cvArray) {
         const values = ['user_DR_MediaResourceID', 'user_DR_Primary'];
        let callvariableNames = [];
        let areVariablesPresent = true;
            for (const callVariable of cvArray){
            callvariableNames.push(callVariable.name);
            if (callVariable.name === 'user_DR_MediaResourceID' || callVariable.name === 'user_DR_Primary') {
                if (!callVariable.value) {
                    areVariablesPresent = false;
                    break;
                }
            }
        }
        if (!areVariablesPresent) return areVariablesPresent;
        areVariablesPresent = values.every(value => {
            return callvariableNames.includes(value);
        });
        return areVariablesPresent;
    }

    function createTask(dialog) {
        const dialogInfo = dialog.getData();
        let conversationid;
        let userDRMediaChannelName;
        for (const callVariable of dialogInfo.mediaProperties.callvariables.CallVariable){
            // user_DR_MediaResourceID holds the conversation ID.
            if (callVariable.name === 'user_DR_MediaResourceID') { 
                conversationid = callVariable.value;
            } else if (callVariable.name === 'user_DR_MediaChannelName') { 
                userDRMediaChannelName = callVariable.value;
            }
        }
        const mediaLayout = getMediaLayout(dialog, mediaLayoutRef.current);
        let callVariableArray = dialogInfo.mediaProperties.callvariables.CallVariable;
        let areFieldsPresent = checkIfMandatoryFieldsExist(callVariableArray);
        const taskFromLs = getTaskFromLS(dialog.getData().id);
        console.log(':: usemediasetup createTask. is task present in cache :  ', taskFromLs);

        const task = {
            taskId: dialogInfo.id,
            mediaType: userDRMediaChannelName || dialogInfo.mediaChannel,
            mrdID: dialogInfo.mediaProperties.mediaId,
            title: dialogInfo.mediaProperties[mediaLayout.header.mediaProperty] || dialogInfo.mediaProperties.callvariables.CallVariable[0].value || i18n.getMsg('gadget.imi.task.customername') || 'Customer',
            queue: dialogInfo.mediaProperties.queueName,
            timeStamp: (taskFromLs && Number(taskFromLs.timeStamp)) || new Date().getTime() / 1000,
            transferError: (taskFromLs && taskFromLs.transferError) || false,
            wrapUpApplyError: (taskFromLs && taskFromLs.wrapUpApplyError) || false,
            closeOperationError: (taskFromLs && taskFromLs.closeOperationError) ||  false,
            endOperationSuccess: (taskFromLs && taskFromLs.endOperationSuccess) || false,
            endOperationError: (taskFromLs && taskFromLs.endOperationError) || false,
            endOperationInProgress: (taskFromLs && taskFromLs.endOperationInProgress) || false,
            transferInProgress: (taskFromLs && taskFromLs.transferInProgress) || false,
            disableTransfer: (taskFromLs && taskFromLs.disableTransfer) || false, // used to disbale transfer when chat is closed by customer
            selectedWrapupReason: (taskFromLs && taskFromLs.selectedWrapupReason) || undefined,
            isInterrupted: (taskFromLs && taskFromLs.isInterrupted) || false, 
            displayTime: 0,
            wrapUpTimeout: null,
            conversationid: useFinesseSimulator ?  dialogInfo.id : conversationid,
            areFieldsPresent: useFinesseSimulator ? true : areFieldsPresent,
            quantity: storedNewReplies[useFinesseSimulator ?  dialogInfo.id : conversationid] || 0
        };
        addCacheActions(task);
        addParticipantActions(task, dialog);
        addTimerActions(task);
        console.log(':: usemediasetup createTask. task- ', task, task.conversationid, task.quantity);
        return task;
    }

    function addCacheActions(task) {
        task.makeTaskActiveInLS = () => {saveTaskToLS(ACTIVE_TASK_LS_KEY, task);};

        task.removeTaskFromLS = () => {
            console.log(':: usemediasetup removeTaskFromLS- ');

            removeTaskFromLS(ACTIVE_TASK_LS_KEY, task.taskId);
            removeTaskFromLS(ALL_TASKS_COLECTION_LS_KEY, task.taskId);
            console.log(':: usemediasetup removeTaskFromLS. after removing- ', getFromLS(ACTIVE_TASK_LS_KEY), getFromLS(ALL_TASKS_COLECTION_LS_KEY));
        };

        task.addTaskToCollectionInLS = () => {
            let taskLS = getTaskFromLS(task.taskId);
            if (taskLS) {
                removeTaskFromLS(ALL_TASKS_COLECTION_LS_KEY, task.taskId);
            }
            let alltaskLS = getFromLS(ALL_TASKS_COLECTION_LS_KEY) || [];
            alltaskLS.push(task);
            console.log(':: usemediasetup addTaskToCollectionInLS- ', task, alltaskLS);
            saveTaskToLS(ALL_TASKS_COLECTION_LS_KEY, alltaskLS);
        };

        task.updateTaskInLS = () => {
            console.log(':: usemediasetup updateTaskInLS- ', task);
            task.addTaskToCollectionInLS();
            const activeTaskLS = getFromLS(ACTIVE_TASK_LS_KEY);
            if (activeTaskLS && activeTaskLS.taskId === task.taskId) {
                task.makeTaskActiveInLS();
            }
        }
    }

    function addTimerActions(task) {
        task.timeOutFn = () => {
            console.log(':: usemediasetup timeOutFn closing the task- ', task);
            task.myParticipant.actions['CLOSE'].actionListener();
            clearTimeout(task.wrapUpTimeout);
        };

        task.startTimer = () => {
            const displayTimeout = Math.round(Number(userDataRef.current.wrapUpTimer) - ((window.finesse.utilities.Utilities.currentServerTimeMillis() - (task.stateChangetimeMs || window.finesse.utilities.Utilities.currentServerTimeMillis()))/1000));

            // const displayTimeout = Math.round(Number(userDataRef.current.wrapUpTimer) - ((window.finesse.utilities.Utilities.currentServerTimeMillis() - (task.stateChangetimeMs || window.finesse.utilities.Utilities.currentServerTimeMillis()))/1000));
            console.log(':: usemediasetup startTimer displayTimeout: ', displayTimeout);
            return setTimeout(task.timeOutFn, displayTimeout > 0 ? displayTimeout * 1000 : 0);
        };

        task.handleWrapUpTimeout = () => {
            console.log(":: usemediasetup inside handleWrapUpTimeout.", task, userDataRef.current, task.endOperationSuccess, task.wrapUpTimeout, task && task.endOperationSuccess);

            if (task && task.endOperationSuccess && !task.wrapUpTimeout) {
                if (userDataRef.current && userDataRef.current.isWrapUpRequired) {
                    console.log(":: usemediasetup handleWrapUpTimeout. starting timer ", task);
                    const wrapUpTimeout = task.startTimer();
                    task.wrapUpTimeout = wrapUpTimeout;
                    if (!task.myParticipant.actions['WRAP_UP']) {
                        taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task});
                    } else {
                        task.myParticipant.actions['WRAP_UP'] && task.myParticipant.actions['WRAP_UP'].actionListener(wrapUpTimeout);
                    }
                } else {
                    console.log(":: usemediasetup handleWrapUpTimeout. calling task close action ", task);
                    task.myParticipant.actions['CLOSE'] && task.myParticipant.actions['CLOSE'].actionListener();
                }
            }
        };
    }

    function activateTask(dialog) {
        dialog.setTaskState(finesse.restservices.MediaDialog.TaskActions.START, {
            success: function () {
                if (!processedTasksStatus.current[dialog.getId()]) {
                    console.log(':: usemediasetup processtask- activateTask. adding the task to store', dialog.getId() + ' and storedNewReplies : ' , storedNewReplies);
                    // set the tasklist once the task is moved to active state
                    const task = createTask(dialog);
                    taskListHandleActions({
                        type: TASKLIST_ACTIONS.ADD,
                        task: task
                    });            
                    processedTasksStatus.current[dialog.getId()] = true; // true referes to existing in store
                    task.addTaskToCollectionInLS();
                }
            },
            error: function (err) {
                console.log('Error: Could not move the task to ACTIVE state', err);
            }
        });
    }

    function addTask(dialog) {
        console.log(':: usemediasetup processtask- addTask- is task already added to store? : ' + dialog.getId(), processedTasksStatus.current[dialog.getId()] + ' and storedNewReplies : ' , storedNewReplies);
        if (!processedTasksStatus.current[dialog.getId()]) {
            const task = createTask(dialog);
            taskListHandleActions({
                type: TASKLIST_ACTIONS.ADD,
                task: task
            });
            processedTasksStatus.current[dialog.getId()] = true; // true referes to existing in store
            task.addTaskToCollectionInLS();
        }
    }

    const startTimer = (task) => {
        const displayTimeout = Number(userDataRef.current.wrapUpTimer);
		return setTimeout((task) => {
            task.myParticipant.actions['CLOSE'].actionListener();
            clearTimeout(task.wrapUpTimeout);
        }, displayTimeout * 1000, task);
	}

    function handleWrapUp(task, dialog, myParticipant) {
        console.log(':: usemediasetup handleWrapUp arguments- ', task, dialog, myParticipant);
        const util = window.finesse.utilities.Utilities;
        let stateChangeTime = myParticipant.stateChangeTime;
        let stateChangetimeMs = util.extractTime(stateChangeTime);
        let rePopulateTask = false;
        if (!task) {
            task = createTask(dialog);
            rePopulateTask = true;
        }
        const taskLS = getTaskFromLS(task.taskId);
        task.stateChangetimeMs = (taskLS && Number(taskLS.stateChangetimeMs)) || stateChangetimeMs || new Date().getTime();
        console.log(':: usemediasetup handleWrapUp. task fetched from cache', taskLS);

        task.displayTime = Math.round(
            (util.currentServerTimeMillis() - (task.stateChangetimeMs || new Date().getTime())) / 1000
        );
        task.endOperationSuccess = true;
        task.endOperationError = false;
        task.endOperationInProgress = false;
        if (rePopulateTask) {
            taskListHandleActions({ type: TASKLIST_ACTIONS.ADD, task });
            task.addTaskToCollectionInLS();
        } else {
            taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
            task.addTaskToCollectionInLS();
        }
        console.log('::- useMediaSetup- handleWrapUp. rePopulateTask : ', rePopulateTask, task.endOperationSuccess, task);
        console.log('::- useMediaSetup- handleWrapUp. processedTasksStatus : ', processedTasksStatus.current, processedTasksStatus.current[dialog.getId()]);
        task.handleWrapUpTimeout();
        task.updateTaskInLS();
    }


    function addParticipantActions (task, dialog) {
        let actions, actionData;
        const myParticipant = getMyParticipant(dialog);
        // Build task actions from participant object and enable buttons based on
        // the task actions.
        if (myParticipant.actions === null) {
            actionData = [];
        } else {
            actions = myParticipant.actions.action;

            if (typeof actions === 'string') {
                actionData = [actions];
            } else {
                actionData = actions;
            }
        }
        var j, action;
        task.myParticipant = { actions: {} };
        // Reset wrapup error flag everytime when update happens
        task.wrapUpApplyError = false;
        let options = {
            error: (response) => {
                let existingTask = isExistingTask(dialog);
                if (!existingTask) {
                    return;
                }
                task.wrapUpApplyError = true;
            }
        };
        task.myParticipant.actions.APPLY_WRAP_UP = {
            action: 'APPLY_WRAP_UP',
            actionListener: (wrToApply) => {
                task.selectedWrapupReason = wrToApply;
                taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                // dialog.updateWrapUpReason(wrToApply, options);
            }
        };
        console.log('::- useMediaSetup- addParticipantActions. : ', task, actionData, myParticipant);
		
        for (j = 0; j < actionData.length; j = j + 1) {
            action = actionData[j];

            switch (action) {
            case _participantActions.WRAP_UP:
                task.myParticipant.actions[_participantActions.WRAP_UP] = {
                    action: 'WRAP_UP',
                    actionListener: () => {
                        task.endOperationInProgress = true;
                        taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                        dialog.setTaskState(finesse.restservices.MediaDialog.TaskActions.WRAP_UP, {
                            success: () => { console.log('moved to wrap-up state successfully'); },
                            error: () => {
                                task.endOperationSuccess = false;
                                task.endOperationError = true;
                                task.endOperationInProgress = false;
                                taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                            }
                        },
                        null
                        );
                    }
                };
				task.myParticipant.actions['UPDATE_CALL_DATA'] = {
                    action: 'UPDATE_CALL_DATA',
                    actionListener: (callVariablePayLoad) => {
                        console.log('UPDATE_CALL_DATA');
                        _useCallVariableProps._handleCallVariableUpdate(callVariablePayLoad, dialog, task);
                }
				};
                break;
            case _participantActions.TRANSFER:
                task.myParticipant.actions[_participantActions.TRANSFER] = {
                    action: _participantActions.TRANSFER,
                    actionListener: (dialedNumber) => {
                        task.transferInProgress = true;
                        task.transferError = false;
                        taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                        dialog.transferWithWrapUp(task.selectedWrapupReason, dialedNumber.number, {
                            success: () => {
                                task.transferSuccess = true;
                                task.transferInProgress = false;
                                taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                            },
                            error: (rsp) => {
                                console.log("transfer error", rsp);
                                let errorCode = '';
                                if (rsp) {
                                    // Async Finesse errors.
                                    if (rsp.status === 400 && rsp.object && rsp.object.ApiErrors) {
                                        let apiError = rsp.object.ApiErrors.ApiError;
                                        if (apiError && apiError.ErrorType !== undefined) {
                                            errorCode = 'CTI-' + apiError.ErrorData;
                                        } else {
                                            errorCode = '-1';
                                        }
                                        // HTTP 503 errors.
                                    } else if (rsp.status !== undefined) {
                                        errorCode = 'HTTP-' + rsp.status;
                                        // All other errors.
                                    } else {
                                        errorCode = '-1';
                                    }
                                } else {
                                    errorCode =  '-1';
                                }
                                task.transferError = true;
                                task.transferErrorCode = errorCode;
                                task.transferSuccess = false;
                                task.transferInProgress = false;
                                taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                            }
                        });
                    }
                };
				 task.myParticipant.actions['UPDATE_CALL_DATA'] = {
                    action: 'UPDATE_CALL_DATA',
                    actionListener: (callVariablePayLoad) => {
                        _useCallVariableProps._handleCallVariableUpdate(callVariablePayLoad, dialog, task);
                }
				};
                break;
            case _participantActions.CLOSE:
                task.myParticipant.actions[_participantActions.CLOSE] = {
                    action: _participantActions.CLOSE,
                    actionListener: (e) => {
                        if (e) {
                            processedTasksStatus.current[dialog.getId()] = true;
                        }
                        task.endOperationInProgress = true;
                        taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                        const handlers = {
                            success: () => {
                                clearTimeout(task.wrapUpTimeout);
                                task.endOperationInProgress = false;
                                taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                            },
                            error: () => {
                                processedTasksStatus.current[dialog.getId()] = true;
                                task.closeOperationError = true;
                                task.endOperationInProgress = false;
                                taskListHandleActions({ type: TASKLIST_ACTIONS.UPDATE, task });
                            }
                        };
                        if (task.selectedWrapupReason && processedTasksStatus.current[dialog.getId()]) {
                            dialog.closeWithWrapUp(task.selectedWrapupReason, handlers, null);
                            // processedTasksStatus.current[dialog.getId()] = false;
                        } else {
                            if (processedTasksStatus.current[dialog.getId()]) {
                                dialog.setTaskState(finesse.restservices.MediaDialog.TaskActions.CLOSE, handlers, null);
                            }
                        }
                    }
                };
                break;
            }


        }
    	_useCallVariableProps._updateCallVariables(dialog.getMediaProperties(), task, task.myParticipant, dialog, mediaPropertiesLayouts, user);
    }
    
    /**
     * Update the selected wrapup reason inside the task object
     */
	function _updateWrapUpReason (task, dialog) {
        var mediaProperties = dialog.getMediaProperties();
        const _util = window.finesse.utilities.Utilities;
        if (mediaProperties && mediaProperties.wrapUpReason) {
            let incomingWR = _util.translateHTMLEntities(mediaProperties.wrapUpReason, true);
            task.selectedWrapupReason = incomingWR;
        }
    }

    function shouldHandleWrapUpDialog (dialog) {
        if (!processedTasksStatus.current.hasOwnProperty(dialog.getId())) {
            processedTasksStatus.current[dialog.getId()] = true;
        }
        if (processedTasksStatus.current.hasOwnProperty(dialog.getId()) && processedTasksStatus.current[dialog.getId()]) {
            return true;
        }
        return false;
    }
    
    function processTask(dialog) {
        let task = isExistingTask(dialog);
        console.log(':: usemediasetup processTask. check if it is a existing task: ', task);

        if (!task) {
            dialog.addHandler('change', processTask);
        }
        const myParticipant = getMyParticipant(dialog);
        if (!myParticipant) {
            return;
        }
        const myCallState = myParticipant.state;

        if (task) {
            addParticipantActions(task, dialog);
            _updateWrapUpReason(task, dialog);
        }
        console.log(':: usemediasetup processTask- ', dialog, myCallState);

        if (task && myCallState === taskStates.ACTIVE) {
            _useCallVariableProps.checkSuccess(task, dialog.getMediaProperties());
        }

        switch(myCallState) {
            case taskStates.OFFERED:
                mediaPopOverSetup.showPopOver(dialog);
                break;
            case taskStates.ACCEPTED:
                mediaPopOverSetup.dismissPopOver(dialog);
                activateTask(dialog);
                break;
            case taskStates.ACTIVE:
                    addTask(dialog);
                break;
            case taskStates.WRAPPING_UP:
                console.log('::- useMediaSetup- processTask[WRAPPING_UP]. should call handleWarpup for the dialog : ', shouldHandleWrapUpDialog(dialog));

                // processedTasksStatus.current[dialog.getId() as false means the task has been closed/deleted.
                // condition to prevent multiple close requests on same dialog because during failback, same dialog has been fetched more than once
                if (shouldHandleWrapUpDialog(dialog)) {
                    handleWrapUp(task, dialog, myParticipant);
                }
                break;
        }

    }
    

    function handleMediaDialogsAdd(dialog) {
        processTask(dialog);
    }

    function handleMediaDialogsDelete(dialog) {
        // TODO: Implement
        console.log('::- useMediaSetup- handleMediaDialogsDelete- ', dialog);
        const task = isExistingTask(dialog);
        if (task) {
            clearTimeout(task.wrapUpTimeout);
            taskListHandleActions({ type: TASKLIST_ACTIONS.REMOVE, task });
            processedTasksStatus.current[dialog.getId()] = false; // false referes to task has been deleted
            task.removeTaskFromLS();
        }
    }

    /**
     * Handler for when GET WrapUpReasons has loaded
     *
     * @private
     */
    function handleWrapUpReasonsLoaded (reasons) {
        if (reasons) {
            wrapUpReasons = reasons;
        }
        var wrapUpReasonsCollection = wrapUpReasons.getCollection(), wrapUpReasonLabels = [];
        const _wrapUp = {};
        _wrapUp.options = [];

        $.each(wrapUpReasonsCollection, function (i, wrapUpReason) {
            let option = {};
            // Explicitly store the labels as strings so they can be properly string-sorted later
            wrapUpReasonLabels.push(String(wrapUpReason.getLabel()));
            option.label = String(wrapUpReason.getLabel());
            option.value = window.finesse.utilities.Utilities.translateHTMLEntities(wrapUpReason.getLabel(), true);
            _wrapUp.options.push(option);
        });
        let caseInsensitiveSort = (a, b) => {
            let emptyString = '';
            a = a.label + emptyString;
            b = b.label + emptyString;
            a = a.toLowerCase();
            b = b.toLowerCase();
            return a > b ? 1 : (a < b ? -1 : 0);
        };

        // If at least one wrap-up reason is configured, initialize every existing wrap-up reason control group.
        // This only applies to in-progress calls when the agent logs into the desktop, initialization for new
        // calls is handled inside _processCall().
        if (_wrapUp.options.length > 0) {
            // Sort the labels BEFORE they're converted to HTML entities for display, otherwise we'll be sorting HTML entities
            _wrapUp.options.sort(caseInsensitiveSort);
        }
        setWrapUpData({wrapUpReasons: _wrapUp});
    }

    /**
     * Handler for when GET WrapUpReasons has errors
     *
     * @private
     */
    function handleWrapUpReasonsError () {
        const _wrapUp = {};
        // Dispatach an error for handling error for wrap up reason
        _wrapUp.errorMessage = i18n.getMsg('gadget.callcontrol.error.user.wrapup-reasons');
        setWrapUpData({wrapUpReasons: _wrapUp});
    }

    function handleScriptSelectorsLoaded (response) {
        const selectors =  response.getScriptSelectors();
        const _scriptSelectorObj = {};
        _scriptSelectorObj.options = [];

        $.each(selectors, function (i, selector) {
            _scriptSelectorObj.options.push(selector._data);
        });
        let caseInsensitiveSort = (a, b) => {
            let emptyString = '';
            a = a.label + emptyString;
            b = b.label + emptyString;
            a = a.toLowerCase();
            b = b.toLowerCase();
            return a > b ? 1 : (a < b ? -1 : 0);
        };

        if (_scriptSelectorObj.options.length > 0) {
            // Sort the labels BEFORE they're converted to HTML entities for display, otherwise we'll be sorting HTML entities
            _scriptSelectorObj.options.sort(caseInsensitiveSort);
        }
        setScriptSelectorData({scriptSelectors: _scriptSelectorObj});
    }

    /**
     * Handler for when GET WrapUpReasons has errors
     *
     * @private
     */
    function handleScriptSelectorsError () {
        const _scriptSelectorObj = {};
        // Dispatach an error for handling error for wrap up reason
        _scriptSelectorObj.errorMessage = i18n.getMsg('gadget.callcontrol.contactlist.error');
        setScriptSelectorData({scriptSelectors: _scriptSelectorObj});
    }

    function handleMediaOnFailback (data) {
        console.log('::-  useMediaSetup handleMediaOnFailback. data : ', data, data.status);
        if (data.status === "recovered") {
            digitalChannelSetup.handleFailback(data);
            recoveredRef.current = true;
            processedTasksStatus.current = {}; // reset the flags post recovery.
            storedNewReplies = JSON.parse(localStorage.getItem('unreadmessages')) || {}; // refetch from cache because cache will be updated when desktop disconnected
            console.log('::-  useMediaSetup. recovered is true and now clearing store****');
            taskListHandleActions({type: TASKLIST_ACTIONS.CLEAR});

            // settimeout to make the task back to avtive which was active before failback.
            // timeout of 1000ms wont be visible to agent because there will be a spinner(caused by setMediaRestoring state) for 2secs for restoring the states if needed. To put simply, after failback spinner will be displayed for atleast 2secs
            setTimeout(() => {
                const activeTaskLS = getFromLS(ACTIVE_TASK_LS_KEY);
                const task = taskListStateRef.current.taskList && taskListStateRef.current.taskList.find((task) => task.taskId === activeTaskLS.taskId);
                if (task) {
                    console.log(':: useMediaSetup- After failback, making the task active ', task);
                    taskListHandleActions({ type: TASKLIST_ACTIONS.SELECT, task: task });
                }
            }, 1000);
        }
    }
    function cacheMediaStates () {
        digitalChannelSetup.cacheMediaStates();
    }

    function updateCacheBeforeDisconnect () {
        console.log('::-  useMediaSetup- updateCacheBeforeDisconnect', taskListStateRef.current.taskList);

        const unreadMessagesObj = taskListStateRef.current.taskList.reduce((retObj, task) => {
            return {...retObj, [task.conversationid]: task.quantity}
        }, {});
        saveToLS("unreadmessages", unreadMessagesObj);
        let task = taskListStateRef.current.taskList.find(task => task.taskId === taskListStateRef.current.selectedTaskId);
        activateNewTasksAutomatically && task && task.makeTaskActiveInLS();
    }

    return {
        state: {
            loading,
            signedIn,
            retry,
            error,
            conversationLoader,
            errorAfterTaskLoad
        },
        initialize,
        handleMediaLogin,
        FNCRegistration,
        finessePopoverRegistration,
        configureAllAvailableMedia,
        setConversationLoader,
        setErrorAfterTaskLoad,
        user,
        wrapUpData,
        userData,
        scriptSelectorData,
        handleMediaOnFailback,
        cacheMediaStates,
        updateCacheBeforeDisconnect
    }
}

export default useMediaSetup;
